/* ==========================================================================
   HAZZY APPAREL — Dawn theme custom behavior
   Talks to Shopify's real cart (/cart/add.js, /cart.js) — no fake state.
   This file owns the cart drawer's open/close/remove behavior directly
   (see openCartDrawer/closeCartDrawer below) rather than depending on
   Dawn's own <cart-drawer> custom element, since that element only works
   if Dawn's unmodified cart-drawer.js is also present and registers it —
   fragile across Dawn versions. This file adds: cart drawer control,
   sticky ATC bar, bundle upsell, reveal-on-scroll, mobile nav, accordion.
   ========================================================================== */

(function(){
  "use strict";

  console.log('[Hazzy] hazzy-custom.js loaded'); // remove once everything is confirmed working

  /* ---------- Mobile nav ---------- */
  const navToggle = document.querySelector('.nav-toggle');
  const mobileNav = document.querySelector('.mobile-nav');
  const mobileNavClose = document.querySelector('.mobile-nav-close');

  if (navToggle && mobileNav){
    navToggle.addEventListener('click', () => mobileNav.classList.add('open'));
    mobileNavClose?.addEventListener('click', () => mobileNav.classList.remove('open'));
    mobileNav.querySelectorAll('a').forEach(a => a.addEventListener('click', () => mobileNav.classList.remove('open')));
  }

  /* ---------- Sticky header shadow-on-scroll ---------- */
  const header = document.querySelector('.site-header');
  window.addEventListener('scroll', () => {
    if (!header) return;
    header.style.boxShadow = window.scrollY > 8 ? '0 8px 24px rgba(0,0,0,0.25)' : 'none';
  }, { passive:true });

  /* ---------- Cart drawer: open / close / remove-item ----------
     Fully self-contained — no dependency on Dawn's <cart-drawer> custom
     element. Toggles the same .open class the CSS already expects on
     #cart-drawer / #cart-overlay (see assets/hazzy-custom.css). ---------- */
  const cartDrawerEl = document.getElementById('cart-drawer');
  const cartOverlayEl = document.getElementById('cart-overlay');
  const cartIconBtn = document.querySelector('[data-cart-icon-bubble]');
  const cartDrawerCloseBtn = document.getElementById('cart-drawer-close-btn');

  function openCartDrawer(){
    cartDrawerEl?.classList.add('open');
    cartOverlayEl?.classList.add('open');
    cartDrawerEl?.setAttribute('aria-hidden', 'false');
  }
  function closeCartDrawer(){
    cartDrawerEl?.classList.remove('open');
    cartOverlayEl?.classList.remove('open');
    cartDrawerEl?.setAttribute('aria-hidden', 'true');
  }
  window.HazzyOpenCart = openCartDrawer;
  window.HazzyCloseCart = closeCartDrawer;

  if (cartIconBtn){
    cartIconBtn.addEventListener('click', (e) => {
      e.preventDefault(); // stop navigation to /cart — href stays as a no-JS fallback
      openCartDrawer();
    });
  }
  cartDrawerCloseBtn?.addEventListener('click', closeCartDrawer);
  cartOverlayEl?.addEventListener('click', closeCartDrawer);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCartDrawer(); });

  /* ---------- Reveal-on-scroll ---------- */
  const revealEls = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && revealEls.length){
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting){
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    revealEls.forEach(el => io.observe(el));
  } else {
    revealEls.forEach(el => el.classList.add('in'));
  }

  /* ---------- Toast (lightweight confirmation, separate from
     Dawn's own cart-notification bubble — used for wishlist,
     bundle-add, etc. where we don't want to open the drawer) ---------- */
  let toastTimer;
  function showToast(msg){
    let toast = document.querySelector('.toast');
    if (!toast){
      toast = document.createElement('div');
      toast.className = 'toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 2400);
  }
  window.HazzyToast = showToast;

  /* ---------- Helper: refresh the cart drawer + icon bubble after an
     AJAX add/remove. Opens immediately (no waiting on network) and fills
     in real content once the single section-render fetch resolves. ---------- */
  const cartDrawerSectionId = document.getElementById('cart-drawer')?.dataset.sectionId;

  function refreshCartUI(openDrawer){
    if (openDrawer) openCartDrawer(); // open right away — don't make the user wait on a fetch

    if (!cartDrawerSectionId) return;

    fetch('/?sections=' + cartDrawerSectionId)
      .then(r => r.json())
      .then(sections => {
        const html = sections[cartDrawerSectionId];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newShipping = doc.querySelector('.cart-drawer-shipping');
        const oldShipping = document.querySelector('.cart-drawer-shipping');
        if (newShipping && oldShipping) oldShipping.outerHTML = newShipping.outerHTML;
        else if (newShipping && !oldShipping) document.querySelector('.cart-drawer-head')?.insertAdjacentHTML('afterend', newShipping.outerHTML);
        else if (!newShipping && oldShipping) oldShipping.remove();

        const newBody = doc.getElementById('CartDrawer-CartItems');
        const oldBody = document.getElementById('CartDrawer-CartItems');
        if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;

        const newFoot = doc.querySelector('.cart-drawer-foot');
        const oldFoot = document.querySelector('.cart-drawer-foot');
        if (newFoot && oldFoot) {
          oldFoot.innerHTML = newFoot.innerHTML;
          oldFoot.style.display = newFoot.style.display; // copy shown/hidden state too, not just contents
        }

        fetch('/cart.js').then(r => r.json()).then(cartData => {
          document.querySelectorAll('.cart-count').forEach(el => el.textContent = cartData.item_count);
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartData }));
        });
      })
      .catch(() => showToast('Could not refresh cart'));
  }
  window.HazzyRefreshCart = refreshCartUI;

  // Event delegation for remove-buttons and checkout link — bound once,
  // works even after the drawer's inner HTML gets swapped by refreshCartUI.
  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-remove-cart-key]');
    if (removeBtn){
      const key = removeBtn.getAttribute('data-remove-cart-key');
      removeBtn.setAttribute('disabled', 'disabled');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      })
        .then(r => r.json())
        .then(() => refreshCartUI(false))
        .catch(() => { removeBtn.removeAttribute('disabled'); showToast('Could not remove item — please try again'); });
      return;
    }
    const checkoutBtn = e.target.closest('[data-checkout-link]');
    if (checkoutBtn){
      e.preventDefault();
      window.location.href = '/checkout';
    }
  });

  /* ---------- Size chart modal ---------- */
  const sizeChartModal = document.getElementById('size-chart-modal');
  const sizeChartOverlay = document.getElementById('size-chart-overlay');
  if (sizeChartModal){
    function openSizeChart(){
      sizeChartModal.classList.add('open');
      sizeChartOverlay?.classList.add('open');
      sizeChartModal.setAttribute('aria-hidden', 'false');
    }
    function closeSizeChart(){
      sizeChartModal.classList.remove('open');
      sizeChartOverlay?.classList.remove('open');
      sizeChartModal.setAttribute('aria-hidden', 'true');
    }
    document.querySelectorAll('[data-show-size-chart]').forEach(btn => {
      btn.addEventListener('click', openSizeChart);
    });
    document.getElementById('size-chart-close-btn')?.addEventListener('click', closeSizeChart);
    sizeChartOverlay?.addEventListener('click', closeSizeChart);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSizeChart(); });
  }

  /* ---------- Accordion (PDP) ---------- */
  document.querySelectorAll('.acc-item').forEach(item => {
    const head = item.querySelector('.acc-head');
    const body = item.querySelector('.acc-body');
    head?.addEventListener('click', () => {
      const isOpen = item.classList.contains('open');
      document.querySelectorAll('.acc-item.open').forEach(other => {
        other.classList.remove('open');
        other.querySelector('.acc-body').style.maxHeight = null;
      });
      if (!isOpen){
        item.classList.add('open');
        body.style.maxHeight = body.scrollHeight + 'px';
      }
    });
  });

  /* ============================================================
     PRODUCT PAGE
     ============================================================ */
  const pdpForm = document.querySelector('.pdp-form');
  if (pdpForm){

    /* ---------- Gallery: swap main image by data-media-id ---------- */
    const gallery = document.querySelector('.gallery');
    if (gallery){
      const mainImgs = gallery.querySelectorAll('.gallery-main img');
      const thumbs = gallery.querySelectorAll('.gallery-thumbs button');
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const id = thumb.getAttribute('data-media-id');
          thumbs.forEach(t => t.classList.remove('active'));
          mainImgs.forEach(img => img.classList.remove('active'));
          thumb.classList.add('active');
          gallery.querySelector('.gallery-main img[data-media-id="' + id + '"]')?.classList.add('active');
        });
      });
    }

    /* ---------- Variant data, parsed from the JSON blob the
       product-form snippet renders (see snippets/hazzy-product-form.liquid) ---------- */
    let variants = [];
    const variantDataEl = document.getElementById('variant-data-' + pdpForm.dataset.sectionId);
    if (variantDataEl){
      try { variants = JSON.parse(variantDataEl.textContent); } catch(e){ variants = []; }
    }

    const variantIdInput = pdpForm.querySelector('input[name="id"]');
    const priceEls = document.querySelectorAll('[data-pdp-price]');
    const addBtn = document.getElementById('add-to-cart-' + pdpForm.dataset.sectionId);
    const sizeWarn = document.querySelector('.size-warn');
    let selectedOptions = {}; // e.g. { Size: 'M', Color: 'Black' }

    function findVariant(){
      return variants.find(v =>
        v.options.every((optVal, i) => {
          const optName = pdpForm.dataset['option' + i];
          return !selectedOptions[optName] || selectedOptions[optName] === optVal;
        })
      );
    }

    function money(cents){
      return '$' + (cents / 100).toFixed(2);
    }

    function syncVariantUI(){
      const match = findVariant();
      if (!match) return;
      if (variantIdInput) variantIdInput.value = match.id;
      if (priceEls.length) priceEls.forEach(el => el.textContent = money(match.price));
      if (addBtn){
        addBtn.disabled = !match.available;
        addBtn.querySelector('span').textContent = match.available
          ? 'Add to Cart — ' + money(match.price)
          : 'Sold Out';
      }
      // swap gallery to this variant's featured image, if it has one
      if (match.featured_media_id){
        document.querySelector('.gallery-thumbs button[data-media-id="' + match.featured_media_id + '"]')?.click();
      }
    }

    // size / option buttons (rendered by hazzy-product-form.liquid as
    // .size-btn — mirrors the visual language of the original preview
    // rather than Dawn's default radio-pill markup)
    document.querySelectorAll('.opt-block [data-option-name]').forEach(group => {
      const optionName = group.getAttribute('data-option-name');
      group.querySelectorAll('.size-btn').forEach(btn => {
        if (btn.disabled) return;
        btn.addEventListener('click', () => {
          group.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          selectedOptions[optionName] = btn.getAttribute('data-value');
          sizeWarn?.classList.remove('show');
          syncVariantUI();

          // mirror into the sticky bar's mini size row, if present
          const stickyRow = document.getElementById('sticky-size-row');
          if (stickyRow){
            stickyRow.querySelectorAll('.size-btn').forEach(b => {
              b.classList.toggle('active', b.getAttribute('data-value') === selectedOptions[optionName]);
            });
          }
        });
      });
    });

    // quantity stepper
    const qtyRow = pdpForm.querySelector('.quantity, .qty-row');
    if (qtyRow){
      const input = qtyRow.querySelector('input');
      qtyRow.querySelector('[data-qty="minus"]')?.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value || '1', 10) - 1);
      });
      qtyRow.querySelector('[data-qty="plus"]')?.addEventListener('click', () => {
        input.value = Math.min(10, parseInt(input.value || '1', 10) + 1);
      });
    }

    // intercept submit → AJAX add, then refresh + open Dawn's real drawer
    pdpForm.addEventListener('submit', function(e){
      e.preventDefault();

      const requiredGroups = document.querySelectorAll('.opt-block [data-option-name]');
      const missing = Array.from(requiredGroups).some(g => !g.querySelector('.size-btn.active'));
      if (missing){
        sizeWarn?.classList.add('show');
        document.querySelector('.opt-block')?.scrollIntoView({ behavior:'smooth', block:'center' });
        return;
      }

      addBtn?.setAttribute('disabled', 'disabled');
      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(pdpForm),
        headers: { 'Accept': 'application/json' }
      })
        .then(r => r.json())
        .then(item => {
          addBtn?.removeAttribute('disabled');
          if (item.status){
            // Shopify returns { status, message, description } on error (e.g. sold out mid-request)
            showToast(item.description || 'Could not add to cart');
            return;
          }
          showToast(item.product_title + ' added to cart');
          refreshCartUI(true);
        })
        .catch(() => { addBtn?.removeAttribute('disabled'); showToast('Something went wrong — please try again'); });
    });

    /* ---------- Sticky add-to-cart bar ---------- */
    const stickyBar = document.getElementById('sticky-atc');
    if (stickyBar && addBtn){
      const io2 = new IntersectionObserver(([entry]) => {
        stickyBar.classList.toggle('show', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io2.observe(addBtn);
    }
    document.getElementById('sticky-add-btn')?.addEventListener('click', () => {
      pdpForm.requestSubmit ? pdpForm.requestSubmit() : pdpForm.dispatchEvent(new Event('submit', { cancelable:true }));
    });

    /* ---------- Upsell / bundle block ---------- */
    const upsellTotalEl = document.getElementById('upsell-total');
    const upsellAddBtn = document.getElementById('upsell-add-btn');
    const upsellChecks = document.querySelectorAll('[data-upsell]');

    function money2(n){ return '$' + n.toFixed(2); }
    function updateUpsellTotal(){
      let total = 0;
      upsellChecks.forEach(chk => { if (chk.checked) total += parseFloat(chk.getAttribute('data-price')); });
      if (upsellTotalEl) upsellTotalEl.textContent = money2(total);
    }
    upsellChecks.forEach(chk => chk.addEventListener('change', updateUpsellTotal));
    updateUpsellTotal();

    upsellAddBtn?.addEventListener('click', () => {
      const items = [];
      upsellChecks.forEach(chk => {
        if (chk.checked){
          items.push({ id: parseInt(chk.getAttribute('data-variant-id'), 10), quantity: 1 });
        }
      });
      if (items.length === 0) return;

      upsellAddBtn.setAttribute('disabled', 'disabled');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      })
        .then(r => r.json())
        .then(() => {
          upsellAddBtn.removeAttribute('disabled');
          showToast(items.length + ' item' + (items.length > 1 ? 's' : '') + ' added to cart');
          refreshCartUI(true);
        })
        .catch(() => { upsellAddBtn.removeAttribute('disabled'); showToast('Something went wrong — please try again'); });
    });
  }

  /* ============================================================
     COLLECTION / HOME GRID — quick add
     Uses Dawn's variant-id data attribute (rendered by the
     product-card snippet) rather than the demo's price-text parsing.
     ============================================================ */
  document.querySelectorAll('[data-quick-add-variant-id]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.getAttribute('data-quick-add-variant-id');
      const name = btn.getAttribute('data-quick-add-name') || 'Item';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      })
        .then(r => r.json())
        .then(() => {
          showToast(name + ' added to cart');
          refreshCartUI(true);
        })
        .catch(() => showToast('Something went wrong — please try again'));
    });
  });

  /* ---------- Newsletter — Dawn's native email signup section
     already posts to Shopify's customer/marketing list; no JS
     needed here. If you keep the custom hazzy-newsletter.liquid
     section instead, its <form> posts natively via the
     {% form 'customer' %} tag — also no JS required. ---------- */

})();

/* ---------- Related products: fetch real Shopify recommendations,
   replace the server-rendered same-collection fallback if data exists ---------- */
(function(){
  document.querySelectorAll('[data-recommendations-url]').forEach(section => {
    const url = section.getAttribute('data-recommendations-url');
    const grid = section.querySelector('.product-grid');
    if (!url || !grid) return;

    fetch(url)
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('.product-grid');
        if (newGrid && newGrid.children.length > 0){
          grid.innerHTML = newGrid.innerHTML;
        }
        // if no recommendations, the server-rendered fallback already in the DOM stays as-is
      })
      .catch(() => { /* keep fallback grid on any fetch error */ });
  });
})();
